import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Factory, Loader2 } from "lucide-react";
import ForgotPassword from "@/components/ForgotPassword";

const LOGIN_BG = "https://images.unsplash.com/photo-1689942010216-dc412bb1e7a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwbWFudWZhY3R1cmluZyUyMHdhcmVob3VzZXxlbnwwfHx8fDE3ODE0MzkzNzh8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("admin@alupipe.com");
  const [password, setPassword] = useState("Admin@123");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const nav = useNavigate();

  if (user && user !== false) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) nav("/");
  };

  return (
    <div className="min-h-screen w-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center">
              <Factory className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-lg font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>Canares Automation</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-[0.2em]">Manufacturing Suite</div>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Welcome back
          </h1>
          <p className="mt-2 text-slate-500 text-sm">Sign in to manage routings, MRP, work orders & quality.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.14em] text-slate-500">Email or employee code</Label>
              <Input
                id="email"
                type="text"
                autoComplete="username"
                autoCapitalize="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.14em] text-slate-500">Password</Label>
                <button type="button" onClick={() => setForgotOpen(true)} className="text-xs text-blue-700 hover:text-blue-800 font-medium" data-testid="forgot-password-link">
                  Forgot password?
                </button>
              </div>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5"
                data-testid="login-password-input"
              />
            </div>
            {error ? (
              <div className="text-sm text-red-600" data-testid="login-error">{error}</div>
            ) : null}
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 transition-all"
              disabled={busy}
              data-testid="login-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sign in
            </Button>
          </form>

          <ForgotPassword open={forgotOpen} onOpenChange={setForgotOpen} initial={email} />

        </div>
      </div>

      {/* Right: hero */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={LOGIN_BG} alt="warehouse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(11,17,32,0.75), rgba(37,99,235,0.35))" }} />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="text-[11px] uppercase tracking-[0.3em] text-blue-200">Integrated Suite</div>
          <h2 className="mt-3 text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
            BOM · Routing · MRP · Quality
          </h2>
          <p className="mt-3 text-slate-200 max-w-md text-sm">
            Plan, produce, and inspect aluminium pipefittings end-to-end — with AI-assisted defect
            triage and material requirement insights.
          </p>
        </div>
      </div>
    </div>
  );
}
